import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import BackEndService from "../../api/BackEndService";
import PropertyService from "../../api/PropertyService";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { sortProperties } from "../../services/ExtractObjectName";
import { generateUUIDFromID } from "../../services/UUIDGeneratorService";
import { RootState } from "../../store";
import { propertyActions } from "../../store/property-slice";
import { searchActions } from "../../store/search-slice";
import { creatingTaskActions } from "../../store/task-slice";
import { Info } from "../Info/Info";
import { List } from "../List/List";
import PullToRefresh from "../PullToRefresh/PullToRefresh";
import { SubMenu } from "../SubMenu/SubMenu";

import styles from "./task-form.module.css";

export const StepTwo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState<number>(3);
  const searchValue = useSelector((state: RootState) => state.search.value);
  const [filteredProperties, setFilteredProperties] = useState<any[]>([]);
  const task = useSelector((state: RootState) => state.task);

  useEffect(() => {
    if (task?.customer?.properties?.length === 0) {
      const redirectTimeout = setTimeout(() => {
        dispatch(creatingTaskActions.resetAppointment());
        navigate("customerinsight");
      }, 3000);

      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      // Clean up the timeout and interval to avoid memory leaks
      return () => {
        clearTimeout(redirectTimeout);
        clearInterval(interval);
      };
    }
  }, [dispatch, task?.customer?.properties?.length]);

  useEffect(() => {
    const properties = task.customer.properties || [];

    const filtered =
      searchValue.length >= 3
        ? properties.filter((object: any) =>
            `${object.street} ${object.zip ?? object.postcode} ${object.city} ${
              object.name1 ?? ""
            }`
              .toLowerCase()
              .includes(searchValue.toLowerCase())
          )
        : properties;

    const sorted = sortProperties(filtered);
    setFilteredProperties(sorted);
  }, [searchValue, task.customer.properties]);

  const onSelectObject = (object: any) => {
    dispatch(creatingTaskActions.setProperty({ property: object }));
    dispatch(
      creatingTaskActions.setAdditionalDataProperty({
        property: {
          id: object.id,
          address: object.name,
          name: object.objectName,
          isSevdeskProperty: object.isSevdeskProperty,
        },
      })
    );

    dispatch(searchActions.setIsExpanded(false));
    dispatch(searchActions.setSearchValue(""));
    const isUUID = (id: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        id
      );

    const propertyId = isUUID(object.id?.toString()!)
      ? object.id
      : generateUUIDFromID(object.id?.toString()!);
    dispatch(propertyActions.setPropertyId(propertyId));
    dispatch(propertyActions.setViewOnly(true));
    navigate(`/properties/${propertyId}/viewonly`);
  };

  const fetchCustomerProperties = async () => {
    try {
      const customerId = generateUUIDFromID(task.customer.id?.toString()!);

      const sevdeskProperties = await BackEndService.GetObjects(
        task.customer.id
      );
      const properties = await PropertyService.GetProperties({
        "filter[customerId]": customerId,
      });

      const missingSevdeskProperties = sevdeskProperties.data.filter(
        (sevdeskProperty) =>
          !properties.data.some(
            (property) =>
              property.id === generateUUIDFromID(sevdeskProperty.id.toString())
          )
      );

      dispatch(
        creatingTaskActions.setCustomerProperties({
          customerProperties: [...properties.data, ...missingSevdeskProperties],
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handlePullToRefresh = (callback: Function) => {
    fetchCustomerProperties()
      .then(() => {
        callback();
      })
      .catch((error) => {
        callback(error);
      });
  };

  const handleGoBack = () => {
    dispatch(creatingTaskActions.setCustomer({ customer: "" }));
    dispatch(
      creatingTaskActions.setAdditionalDataCustomer({
        customer: {
          id: "",
          name: "",
        },
      })
    );
    navigate("/customerinsight");
  };

  return (
    <AppLayout
      className={styles["tasks__list"]}
      withBackButton={false}
      onGoBack={handleGoBack}
    >
      <SubMenu showSearchButton={true}>{t("EnterTask")}</SubMenu>
      <PullToRefresh
        className={styles["customer-refresh"]}
        onRefresh={handlePullToRefresh}
      >
        <List
          subtitle={t("ObjectSelect")}
          data={filteredProperties?.map((object: any) => ({
            id: object.id.toString(),
            name: `${(object.street?.split("#")[0] ?? "").trim()}, ${
              object.zip ?? object.postcode
            } ${object.city}`,
            isSevdeskProperty: object.isSevdeskProperty,
            postcode: object.zip ?? object.postcode,
            street: object.street,
            city: object.city,
            objectName: object.street?.includes("#")
              ? object.street.split("#")[1].trim()
              : object.name1,
            digitalMonitorStatus: object.digitalMonitorStatus,
            digitalMonitorStatusText: object.digitalMonitorStatusText,
          }))}
          onItemSelected={(object: any) => {
            onSelectObject(object);
          }}
        />
        {task?.customer?.properties?.length === 0 && (
          <Info>Keine Standorte vorhanden, redirecting in {countdown}</Info>
        )}
      </PullToRefresh>
    </AppLayout>
  );
};
